import React, { useEffect, useState } from 'react';
import { TopNav } from '../components/TopNav';
import { Sidebar } from '../components/Sidebar';
import { listArtists } from '@/features/artists/services/artists.api';
import { listActivities } from '@/services/activities';
import { apiClient } from '@/services/apiClient';

interface AdminProps {
  theme: string;
  toggleTheme: () => void;
}

type Tab = 'overview' | 'data' | 'settings' | 'debug';

const Admin: React.FC<AdminProps> = ({ theme, toggleTheme }) => {
  const [tab, setTab] = useState<Tab>('overview');
  const [apiOk, setApiOk] = useState<boolean | null>(null);
  const [dbOk, setDbOk] = useState<boolean | null>(null);
  const [artistsCount, setArtistsCount] = useState<number>(0);
  const [activitiesCount, setActivitiesCount] = useState<number>(0);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const h = await apiClient.get<{ ok: boolean }>(`/healthz`);
        setApiOk(!!h.ok);
      } catch {
        setApiOk(false);
      }
      try {
        const hdb = await apiClient.get<{ ok: boolean }>(`/healthz/db`);
        setDbOk(!!hdb.ok);
      } catch {
        setDbOk(false);
      }
      try {
        const la = await listArtists(0, 50);
        setArtistsCount(la.items.length);
      } catch {
        setArtistsCount(0);
      }
      try {
        const act = await listActivities(0, 50);
        setActivitiesCount(act.items.length);
      } catch {
        setActivitiesCount(0);
      }
      try {
        const dbg = await apiClient.get<any>(`/__debug`);
        setDebugInfo(dbg);
      } catch {
        /* ignore outside dev */
      }
    })();
  }, []);

  const StatusPill = ({ ok }: { ok: boolean | null }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
        ok === null
          ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-300/60 dark:border-zinc-700'
          : ok
          ? 'bg-green-500/15 text-green-600 dark:text-green-300 border-green-500/30'
          : 'bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30'
      }`}
    >
      {ok === true ? 'OK' : ok === false ? 'FALHA' : '—'}
    </span>
  );

  return (
    <div className="min-h-screen bg-body overflow-x-hidden">
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <div className="flex">
        <Sidebar activeSection="dashboard" />
        <div className="flex-1">
          <main className="max-w-7xl mx-auto px-4 py-6 pb-20 lg:pb-6">
            <header className="mb-4">
              <h1 className="text-2xl font-extrabold">Painel Administrativo</h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Controles e diagnósticos do ambiente.</p>
            </header>

            <div className="flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 mb-4">
              {(['overview', 'data', 'settings', 'debug'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                    tab === t
                      ? 'border-[#1DB954] text-zinc-900 dark:text-white'
                      : 'border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
                  }`}
                >
                  {t === 'overview' ? 'Visão Geral' : t === 'data' ? 'Dados' : t === 'settings' ? 'Configurações' : 'Debug'}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">API</h3>
                    <StatusPill ok={apiOk} />
                  </div>
                  <p className="text-sm text-zinc-500">/healthz</p>
                </div>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">Banco de Dados</h3>
                    <StatusPill ok={dbOk} />
                  </div>
                  <p className="text-sm text-zinc-500">/healthz/db</p>
                </div>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <h3 className="font-bold mb-1">Resumo</h3>
                  <p className="text-sm text-zinc-500">Artistas: {artistsCount} • Atividades: {activitiesCount}</p>
                </div>
              </section>
            )}

            {tab === 'data' && (
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <h3 className="font-bold">Artistas</h3>
                  <p className="text-sm text-zinc-500 mb-3">{artistsCount} cadastrados</p>
                  <div className="flex gap-2">
                    <a href="/artists" className="px-3 py-2 rounded-md bg-zinc-200 dark:bg-zinc-700 text-sm">Abrir lista</a>
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <h3 className="font-bold">Atividades</h3>
                  <p className="text-sm text-zinc-500 mb-3">{activitiesCount} registros</p>
                  <div className="flex gap-2">
                    <a href="/analytics" className="px-3 py-2 rounded-md bg-zinc-200 dark:bg-zinc-700 text-sm">Abrir análises</a>
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <h3 className="font-bold">Workflow</h3>
                  <p className="text-sm text-zinc-500 mb-3">Tarefas semanais</p>
                  <a href="/workflow" className="px-3 py-2 rounded-md bg-zinc-200 dark:bg-zinc-700 text-sm">Abrir workflow</a>
                </div>
              </section>
            )}

            {tab === 'settings' && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
                  <h3 className="font-bold mb-2">Preferências</h3>
                  <div className="flex items-center justify-between py-2 border-t border-zinc-200/60 dark:border-zinc-800/60">
                    <span className="text-sm">Tema escuro</span>
                    <button onClick={toggleTheme} className="px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-700 text-sm">Alternar</button>
                  </div>
                </div>
              </section>
            )}

            {tab === 'debug' && (
              <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 overflow-auto">
                <h3 className="font-bold mb-2">Config do cookie (dev)</h3>
                <pre className="text-xs whitespace-pre-wrap">{debugInfo ? JSON.stringify(debugInfo, null, 2) : 'Sem info (somente em dev).'}</pre>
              </section>
            )}
          </main>
        </div>
      </div>
      {/* BottomNav removido no Admin para evitar navegação por âncora em páginas sem seções */}
    </div>
  );
};

export default Admin;
